import { IdProps } from 'app/components/Profile/Ids/types';
import { Isni } from 'types/isni.types';
import { Ipn } from 'types/ipn.types';
import { Ipi, ProfileIds } from 'types/ipi.types';
import { IdType } from 'types/enums';
import { getPreferredIds } from 'app/store/Profile/selectors';
import { RowType } from './ProfileIdsTableRow';

export const getIpiOptions = (ipiProps: IdProps<Ipi>, ipi: Ipi) => {
  const canEdit = ipi?._links?.updateIpi;
  const canDelete = ipi?._links?.deleteIpi;
  return [
    ...(canEdit
      ? [
          {
            title: 'Edit',
            value: 'EDIT',
            onClick: () => ipiProps.setIdToUpdate(ipi),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            title: 'Delete',
            value: 'DELETE',
            onClick: () => ipiProps.setIdToDelete(ipi),
          },
        ]
      : []),
  ];
};

export const getIpnOptions = (ipnProps: IdProps<Ipn>, ipn: Ipn) => {
  const canEdit = ipn?._links?.updateIpn;
  const canDelete = ipn?._links?.deleteIpn;
  return [
    ...(canEdit
      ? [
          {
            title: 'Edit',
            value: 'EDIT',
            onClick: () => ipnProps.setIdToUpdate(ipn),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            title: 'Delete',
            value: 'DELETE',
            onClick: () => ipnProps.setIdToDelete(ipn),
          },
        ]
      : []),
  ];
};

export const getIsniOptions = (isniProps: IdProps<Isni>, isni: Isni) => {
  const canEdit = isni?._links?.updateIsni;
  const canDelete = isni?._links?.deleteIsni;
  return [
    ...(canEdit
      ? [
          {
            title: 'Edit',
            value: 'EDIT',
            onClick: () => isniProps.setIdToUpdate(isni),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            title: 'Delete',
            value: 'DELETE',
            onClick: () => isniProps.setIdToDelete(isni),
          },
        ]
      : []),
  ];
};

export const OPTIONS_SIZE = 50;

const getColumnSize = (percent: number, totalWidth?: number) => {
  if (!totalWidth) {
    return undefined;
  }
  return (totalWidth - OPTIONS_SIZE) * percent;
};

export const getColumns = (totalWidth?: number) => [
  {
    accessorKey: 'number',
    header: 'Number',
    minSize: 72,
    size: getColumnSize(0.25, totalWidth),
    enableResizing: true,
  },
  {
    accessorKey: 'idType',
    header: 'Type',
    minSize: 58,
    size: getColumnSize(0.25, totalWidth),
    enableResizing: true,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    size: getColumnSize(0.25, totalWidth),
    minSize: 60,
    enableResizing: true,
  },
  {
    accessorKey: 'society',
    header: 'Society',
    minSize: 68,
    size: getColumnSize(0.25, totalWidth),
    enableResizing: true,
  },
  {
    accessorKey: 'gap',
    size: 0,
  },
  {
    accessorKey: 'options',
    size: OPTIONS_SIZE,
    isOptions: true,
  },
];

export const getNormalizedProfileIds = (
  profileIds: ProfileIds,
  ipiProps: IdProps<Ipi>,
  ipnProps: IdProps<Ipn>,
  isniProps: IdProps<Isni>,
  {
    preferredIpi,
    preferredIpn,
    preferredIsni,
  }: ReturnType<typeof getPreferredIds>
) => {
  const items: RowType[] = [];
  profileIds.ipis.forEach((ipi) => {
    const preferred = preferredIpi?.number === ipi.ipiNameNumber;
    items.push({
      number: ipi.ipiNameNumber,
      verified: ipi.verified,
      preferred,
      displayName: ipi.profile.displayName,
      society: ipi.prSocietyAffiliation?.acronym || '',
      type: IdType.IPI,
      options: getIpiOptions(ipiProps, { ...ipi, default: preferred }),
    });
  });
  profileIds.ipns.forEach((ipn) => {
    const preferred = preferredIpn?.number === ipn.ipnNumber;
    items.push({
      number: ipn.ipnNumber,
      verified: ipn.verified,
      preferred,
      displayName: ipn.profile.displayName,
      society: ipn.cmoSocietyAffiliation?.acronym || '',
      type: IdType.IPN,
      options: getIpnOptions(ipnProps, { ...ipn, default: preferred }),
    });
  });
  profileIds.isnis.forEach((isni) => {
    const preferred = preferredIsni?.number === isni.isni;
    items.push({
      number: isni.isni,
      verified: isni.verified,
      preferred,
      displayName: isni.profile.displayName,
      type: IdType.ISNI,
      options: getIsniOptions(isniProps, { ...isni, default: preferred }),
    });
  });

  return items;
};
